import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { NgOptimizedImage } from '@angular/common';
import { ClientService } from '@dss-libs/shared/data-access/clients';
import { TranslocoDirective } from '@jsverse/transloco';
import { KeycloakAuthService } from '@dss-libs/shared/data-access/auth';
import { LocalstorageConstants, UserRole } from '@dss-libs/shared/type/common-types';
import { Router, RouterModule } from '@angular/router';
import { ModalUiService } from '@dss-libs/shared/utility/common-services';
import { MatIcon } from '@angular/material/icon';
import { SwitchClientModalComponent } from '../switch-client-modal/switch-client-modal.component';
import { AuthorizedRoleDirective } from '@dss-libs/shared/ui/common-directives';
import { LayoutService } from '@dss-libs/portal/utility/portal-utility-services';
import { InitialsPipe } from '@dss-libs/shared/ui/common-pipes';
import { TNavItem } from '@dss-libs/portal/type/portal-types';
import { UserManagementService } from '@dss-libs/shared/data-access/users';

@Component({
  selector: 'dss-libs-feature-sidebar',
  imports: [RouterModule, TranslocoDirective, AuthorizedRoleDirective, MatIcon, InitialsPipe, NgOptimizedImage],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
  readonly #clientService = inject(ClientService);
  readonly #layoutService = inject(LayoutService);
  readonly #keyCloakAuthService = inject(KeycloakAuthService);
  readonly #modalService = inject(ModalUiService);
  readonly #userManagementService = inject(UserManagementService);
  readonly #router = inject(Router);
  readonly sidebarCollapseEvent = output();
  readonly sidebarCollapsePhoneEvent = output();
  isSideBarOpen = this.#layoutService.extendLayout();
  readonly userName = toSignal(
    this.#clientService.resolvedToken$.pipe(map((token) => token.name?.split(' ').slice(0, 2).join(' ') ?? '')),
    { initialValue: '' }
  );
  readonly profilePictureUrl = computed(() => this.#userManagementService.userProfile()?.profilePictureUrl ?? null);
  readonly clientDataResource = this.#clientService.clientDataResource;
  readonly clientData = computed(() => (this.clientDataResource.hasValue() ? this.clientDataResource.value() : null));

  protected readonly UserRole = UserRole;

  readonly navItems = computed<TNavItem[]>(() => [
    {
      route: '/orders',
      labelKey: 'SIDE_BAR.SIGNING',
      activeIcon: 'document-add-bold',
      inactiveIcon: 'document-add',
    },
    {
      route: '/settings',
      labelKey: 'SIDE_BAR.SETTINGS',
      activeIcon: 'settings-bold',
      inactiveIcon: 'settings-outline',
      requiredRole: UserRole.User,
    },
  ]);

  changeLayout() {
    this.isSideBarOpen = !this.isSideBarOpen;
    this.sidebarCollapseEvent.emit();
  }

  changePhoneLayout() {
    this.isSideBarOpen = !this.isSideBarOpen;
    this.sidebarCollapsePhoneEvent.emit();
    this.sidebarCollapseEvent.emit();
  }

  collapseSidebarOnMenuItemClick() {
    if (this.isSideBarOpen) {
      if (window.innerWidth <= 976) {
        this.changePhoneLayout();
      } else {
        this.changeLayout();
      }
    }
  }

  navigateToRoute(route: string) {
    void this.#router.navigate([route]);
    this.collapseSidebarOnMenuItemClick();
  }

  async Logout() {
    window.localStorage.removeItem(LocalstorageConstants.CLIENT_ID);
    await this.#keyCloakAuthService.logout();
  }

  toggleClientsModal() {
    this.#modalService.openAbstractModal({
      component: SwitchClientModalComponent,
      modalDimensionsConfig: { width: '30rem' },
    });
  }
}
